import * as React from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { Search } from 'lucide-react';
import { cn } from 'ui/lib/utils';
import { pushToDataLayer } from '@util/analytics';
import { searchAlgolia } from '@util/firestore/algolia/search.service';
import useDebounce from '@util/hooks/useDebounce';
import { getProductUrl } from '@util/urlHelpers';
import SafeImage from '@ui/SafeImage';
import Avatar from '@ui/Avatar';
import { ProductDocument } from '@util/types/firestore/products';
import { MasterDocument } from '@util/firestore/master';
import { UserDocument } from '@util/types/firestore/users';
import { gfListingsIndex } from './algoliaIndex';

interface SearchBarProps {
  variant?: 'header' | 'standalone';
  placeholder?: string;
  rotatingSuggestions?: string[];
  rotateIntervalMs?: number;
  defaultValue?: string;
  /** Override the default routing behaviour. Receives the trimmed query. */
  onSubmit?: (query: string) => void;
  className?: string;
  name?: string;
  autoFocus?: boolean;
}

// No hardcoded count: "12,000+" overstated a live catalogue of ~4,800 and is
// trivially checkable. Pass a real dynamic count via `placeholder` if one is
// available; otherwise stay honestly vague.
const DEFAULT_PLACEHOLDER = 'Search thousands of cameras, lenses, drones...';
const DEFAULT_SUGGESTIONS = [
  'Sony A7 IV',
  'Canon RF 50mm f/1.2',
  'DJI Mavic 3 Pro',
  'Leica Q3',
  'Fujifilm X-T5',
  'Nikon Z9',
  'Blackmagic Pocket 6K',
];

// GF listings index name is environment-specific — see gfListingsIndex. (The
// GM/GearMaster 'products' index below is a different Algolia app and is
// correct as-is.)
const GF_INDEX = gfListingsIndex();

type AlgoliaHit = { objectID?: string };

/**
 * The marketplace header search. Submitting routes to the Algolia-backed
 * `/search` page; while typing it shows a live autocomplete — Listings and
 * Models side by side, Sellers below. The three queries run independently
 * (GF products, GM/GearMaster models, GF sellers), so each section skeletons
 * and resolves on its own. Sizes are in px because gearfocus-web uses a 10px
 * rem root.
 */
const SearchBar = ({
  variant = 'header',
  placeholder = DEFAULT_PLACEHOLDER,
  rotatingSuggestions = DEFAULT_SUGGESTIONS,
  rotateIntervalMs = 2800,
  defaultValue = '',
  onSubmit,
  className,
  name = 'keyword',
  autoFocus,
}: SearchBarProps) => {
  const router = useRouter();
  const [value, setValue] = React.useState(defaultValue);

  // GB #1401 (bug B): defaultValue only seeded the initial useState — a
  // fresh navigation to a new keyword (e.g. clicking a search suggestion,
  // following a shared /search?keyword= link, or the browser back button)
  // updates the `defaultValue` PROP but this component doesn't remount, so
  // the box kept showing whatever it last held (usually empty) instead of
  // the keyword that actually produced the results on screen. Re-sync
  // whenever the prop itself changes; typing is untouched because `value`
  // (not `defaultValue`) drives onChange, so this never fights the user's
  // own keystrokes.
  React.useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);
  const [focused, setFocused] = React.useState(false);
  const [displayed, setDisplayed] = React.useState(placeholder);
  const [open, setOpen] = React.useState(false);

  const [listingHits, setListingHits] = React.useState<ProductDocument[]>([]);
  const [modelHits, setModelHits] = React.useState<MasterDocument[]>([]);
  const [sellerHits, setSellerHits] = React.useState<UserDocument[]>([]);
  const [loadingListings, setLoadingListings] = React.useState(false);
  const [loadingModels, setLoadingModels] = React.useState(false);
  const [loadingSellers, setLoadingSellers] = React.useState(false);
  // A genuine Algolia failure, distinct from an empty result set.
  // Scoped to the LISTINGS search on purpose. The Models search runs against a
  // separate Algolia app (GM) whose key is a different env var, and
  // searchAlgolia reports a missing key as `error: true` — so a shared flag
  // turned every honest zero-result into "Something went wrong" on any
  // environment where that one key is unset. Models and sellers failing just
  // means those sections don't appear; only a failed listings search is a
  // failure the shopper needs told about.
  const [listingsErrored, setListingsErrored] = React.useState(false);
  // Combobox: index of the keyboard-highlighted option across the flattened
  // Models → Listings → Sellers list (-1 = none highlighted).
  const [activeIndex, setActiveIndex] = React.useState(-1);

  const debounced = useDebounce(value.trim(), 300);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const listboxId = React.useId();

  // Rotating placeholder (paused while focused or with a value).
  React.useEffect(() => {
    if (!rotatingSuggestions.length) return;
    let step = 0;
    const id = window.setInterval(() => {
      if (focused || value) return;
      setDisplayed(
        step % 2 === 0
          ? `Try "${
              rotatingSuggestions[
                Math.floor(step / 2) % rotatingSuggestions.length
              ]
            }"`
          : placeholder
      );
      step++;
    }, rotateIntervalMs);
    return () => window.clearInterval(id);
  }, [focused, value, placeholder, rotatingSuggestions, rotateIntervalMs]);

  // Live autocomplete. Three independent queries rather than one Promise.all,
  // so a slow (or empty) Models search doesn't hold up Listings, and each
  // section can skeleton and pop in on its own. searchAlgolia never throws — it
  // returns an empty set on error — so each .then always settles.
  React.useEffect(() => {
    // GB #1401 gate-review fix (HIGH): this PR seeded `value`'s initial
    // state from a non-empty `defaultValue` (the persisted keyword) instead
    // of always starting ''. `debounced` (useDebounce(value.trim(), 300))
    // seeds its OWN initial state from `value`'s initial state, so this
    // effect previously fired on MOUNT for a dropdown the user never
    // opened — 3 unrequested Algolia queries, x2 SearchBar instances
    // (desktop + mobile) rendered by Navbar, on every /search page view.
    // `open` is only ever set true by an actual focus (with a value) or
    // keystroke (see the input's onFocus/onChange below), so gating on it
    // here ties the autocomplete queries to real user interaction with the
    // box, without touching the debounce delay or the dropdown's own
    // open/close logic.
    if (!open) return;

    let cancelled = false;

    // New term (or cleared): drop any highlighted option and error state.
    setActiveIndex(-1);
    setListingsErrored(false);

    if (!debounced) {
      setListingHits([]);
      setModelHits([]);
      setSellerHits([]);
      setLoadingListings(false);
      setLoadingModels(false);
      setLoadingSellers(false);
      return;
    }

    const hitsPerPage =
      typeof window !== 'undefined' && window.innerWidth <= 768 ? 3 : 5;

    setLoadingListings(true);
    setLoadingModels(true);
    setLoadingSellers(true);

    searchAlgolia('GF', GF_INDEX, {
      query: debounced,
      page: 0,
      hitsPerPage,
      filters: [
        'out_of_stock:false',
        'is_verified:true',
        'is_draft:false',
        'is_expired:false',
      ],
    }).then((r) => {
      if (cancelled) return;
      if (r.error) setListingsErrored(true);
      setListingHits((r.data || []) as ProductDocument[]);
      setLoadingListings(false);
    });

    // GearMaster models live in a separate Algolia app (GM). Needs
    // NEXT_PUBLIC_GEARMASTER_ALGOLIA_SEARCH_KEY set, or this returns empty.
    // `r.error` is deliberately NOT surfaced: an unset key here is a missing
    // section, not a broken search, and treating it as one told every shopper
    // on that environment their query had failed. See listingsErrored.
    searchAlgolia('GM', 'products', {
      query: debounced,
      page: 0,
      hitsPerPage,
    }).then((r) => {
      if (cancelled) return;
      setModelHits((r.data || []) as MasterDocument[]);
      setLoadingModels(false);
    });

    // Exclude inactive stores: Pro, live inventory, or active in the last 6
    // months. Errors ignored for the same reason as the models search above.
    const sixMonthsAgo = Date.now() - 180 * 24 * 60 * 60 * 1000;
    searchAlgolia('GF', 'sellers', {
      query: debounced,
      page: 0,
      hitsPerPage,
      filterString: `is_gearfocus_pro = 1 OR live_listings > 0 OR last_listed > ${sixMonthsAgo} OR created > ${sixMonthsAgo}`,
    }).then((r) => {
      if (cancelled) return;
      setSellerHits((r.data || []) as UserDocument[]);
      setLoadingSellers(false);
    });

    return () => {
      cancelled = true;
    };
  }, [debounced, open]);

  // Close the dropdown on outside pointer/tap.
  React.useEffect(() => {
    const onPointerDown = (e: Event) => {
      const target = e.target as Node | null;
      if (!wrapperRef.current || !target) return;
      if (!wrapperRef.current.contains(target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  // Keep the keyboard-highlighted option in view as it moves through the
  // scrollable dropdown.
  React.useEffect(() => {
    if (activeIndex < 0) return;
    document
      .getElementById(`${listboxId}-opt-${activeIndex}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId]);

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const goToSearch = (query: string) => {
    pushToDataLayer('search', { search_term: query });
    close();
    void router.push(`/search?view=list&keyword=${encodeURIComponent(query)}`);
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const query = value.trim();
    if (!query) return;
    if (onSubmit) {
      onSubmit(query);
      close();
      return;
    }
    goToSearch(query);
  };

  const anyLoading = loadingListings || loadingModels || loadingSellers;
  const hasResults =
    listingHits.length + modelHits.length + sellerHits.length > 0;
  const showDropdown = open && !!value.trim();

  // Flatten the three sections into one option list for keyboard navigation,
  // in the order they read: Models, then Listings, then Sellers.
  const listingBase = modelHits.length;
  const sellerBase = modelHits.length + listingHits.length;
  const optionCount = sellerBase + sellerHits.length;
  const activeOption =
    activeIndex >= 0 && activeIndex < optionCount ? activeIndex : -1;
  const optionId = (index: number) => `${listboxId}-opt-${index}`;
  const optionHref = (index: number): string | undefined => {
    if (index < 0) return undefined;
    if (index < listingBase) return `/m/${modelHits[index].slug}`;
    if (index < sellerBase)
      return getProductUrl(listingHits[index - listingBase].slug);
    return `/shop-seller/${sellerHits[index - sellerBase].username_slug}`;
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      close();
      return;
    }
    if (!showDropdown || optionCount === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => (i + 1) % optionCount);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? optionCount - 1 : i - 1));
    } else if (e.key === 'Enter' && activeOption >= 0) {
      // A highlighted suggestion wins over the default "search this text" submit.
      const href = optionHref(activeOption);
      if (href) {
        e.preventDefault();
        close();
        void router.push(href);
      }
    }
  };

  const sizeClasses =
    variant === 'header'
      ? 'h-[40px] pl-[40px] pr-[16px] text-[14px]'
      : 'h-[48px] pl-[44px] pr-[16px] text-[16px]';

  return (
    <div ref={wrapperRef} className={cn('relative w-full', className)}>
      <form
        role="search"
        onSubmit={handleSubmit}
        data-slot="search-bar"
        data-variant={variant}
        className="relative w-full"
      >
        <Search
          aria-hidden="true"
          size={variant === 'header' ? 16 : 20}
          className="pointer-events-none absolute left-[12px] top-1/2 -translate-y-1/2 text-gray-500"
        />
        <input
          type="search"
          name={name}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setActiveIndex(-1);
            setOpen(true);
          }}
          onFocus={() => {
            setFocused(true);
            if (value.trim()) setOpen(true);
          }}
          onBlur={() => {
            setFocused(false);
            if (!value) setDisplayed(placeholder);
          }}
          onKeyDown={handleKeyDown}
          placeholder={displayed}
          autoFocus={autoFocus}
          aria-label="Search"
          autoComplete="off"
          // Combobox pattern: the input owns the popup listbox and points at the
          // keyboard-highlighted option so screen readers announce it.
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={
            activeOption >= 0 ? optionId(activeOption) : undefined
          }
          className={cn(
            'w-full rounded-full border bg-gray-50 border-gray-200 outline-none transition-colors hover:bg-white hover:border-gray-300 focus:bg-white focus:border-gray-400',
            sizeClasses
          )}
        />
      </form>

      {/* Dropdown is wider than the input on desktop (it may overhang the
          search bar) and full-width in the mobile search row; capped to the
          viewport so it never overflows the right edge. */}
      {showDropdown && (
        <div
          id={listboxId}
          role="listbox"
          aria-label="Search suggestions"
          className="animate-gf-dropdown-in absolute left-0 top-[calc(100%+8px)] z-dropdown max-h-[75vh] w-full max-w-[calc(100vw-24px)] overflow-y-auto overflow-x-hidden rounded-[12px] border border-gray-200 bg-white p-[16px] shadow-lg md:w-[600px] lg:w-[680px]"
        >
          {/* Models first — they're the canonical spec pages that aggregate
              every listing of a model; Listings are the individual items for
              sale, tagged with a price chip so the distinction reads at a glance. */}
          <div className="grid grid-cols-1 gap-[8px] sm:grid-cols-2 sm:gap-[24px]">
            <ResultSection
              title="Models"
              hint="Specs & all listings"
              loading={loadingModels}
              empty={!loadingModels && modelHits.length === 0}
            >
              {modelHits.map((m, i) => (
                <ResultRow
                  key={(m as AlgoliaHit).objectID ?? m.id}
                  id={optionId(i)}
                  active={activeOption === i}
                  onMouseEnter={() => setActiveIndex(i)}
                  href={`/m/${m.slug}`}
                  thumbnail={
                    (m as { thumbnail?: string }).thumbnail ?? m.images?.[0]
                  }
                  title={m.title}
                  onClick={close}
                />
              ))}
            </ResultSection>

            <ResultSection
              title="Listings"
              hint="Items for sale"
              loading={loadingListings}
              empty={!loadingListings && listingHits.length === 0}
            >
              {listingHits.map((p, i) => (
                <ResultRow
                  key={(p as AlgoliaHit).objectID ?? p.id}
                  id={optionId(listingBase + i)}
                  active={activeOption === listingBase + i}
                  onMouseEnter={() => setActiveIndex(listingBase + i)}
                  href={getProductUrl(p.slug)}
                  thumbnail={p.thumbnail}
                  title={p.title}
                  price={p.price}
                  onClick={close}
                />
              ))}
            </ResultSection>
          </div>

          <ResultSection
            title="Sellers"
            loading={loadingSellers}
            empty={!loadingSellers && sellerHits.length === 0}
            className="mt-[12px] border-t border-gray-100 pt-[12px]"
          >
            {sellerHits.map((s, i) => {
              const index = sellerBase + i;
              return (
                <Link
                  key={(s as AlgoliaHit).objectID ?? s.uid}
                  id={optionId(index)}
                  role="option"
                  aria-selected={activeOption === index}
                  onMouseEnter={() => setActiveIndex(index)}
                  href={`/shop-seller/${s.username_slug}`}
                  onClick={close}
                  className={cn(
                    '-mx-[8px] flex items-center gap-[12px] rounded-md px-[8px] py-[6px] transition-colors hover:bg-primary-light',
                    activeOption === index && 'bg-primary-light'
                  )}
                >
                  <span className="shrink-0">
                    <Avatar user={s} size="extraSmall" />
                  </span>
                  <span className="truncate text-[14px] text-gray-800">
                    {s.username}
                  </span>
                </Link>
              );
            })}
          </ResultSection>

          {/* A real Algolia failure is called out distinctly — otherwise it
              renders identically to a genuine empty result. */}
          {!!debounced && !anyLoading && listingsErrored && !hasResults && (
            <p className="px-[8px] py-[8px] text-[13px] text-error">
              Something went wrong loading suggestions. Press Enter to search
              anyway.
            </p>
          )}

          {/* Only claim "No results" once a query has actually run for the
              current term (`debounced` is set) and it wasn't an error. Without
              the `debounced` guard the empty state can't tell "not started yet"
              from "settled empty", so it flashed "No results" for ~236ms before
              the first query even fired. */}
          {!!debounced && !anyLoading && !listingsErrored && !hasResults && (
            <p className="px-[8px] py-[8px] text-[13px] text-gray-500">
              No results for &ldquo;{value.trim()}&rdquo;
            </p>
          )}
        </div>
      )}
    </div>
  );
};

function SectionHeader({
  title,
  hint,
}: {
  title: string;
  hint?: string;
}) {
  return (
    <h3 className="mb-[8px] flex items-baseline justify-between gap-[8px] border-b border-gray-100 pb-[6px]">
      <span className="text-[11px] font-semibold uppercase tracking-[0.6px] text-gray-500">
        {title}
      </span>
      {hint && (
        <span className="text-[10px] font-normal normal-case tracking-normal text-gray-400">
          {hint}
        </span>
      )}
    </h3>
  );
}

/** A labelled result group. Renders skeleton rows while loading; hides itself
 *  entirely when it has settled with nothing (so empty sections don't clutter). */
function ResultSection({
  title,
  hint,
  loading,
  empty,
  className,
  children,
}: {
  title: string;
  hint?: string;
  loading: boolean;
  empty: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  if (empty) return null;
  return (
    <div
      role="group"
      aria-label={title}
      className={cn('animate-gf-fade-up min-w-0', className)}
    >
      <SectionHeader title={title} hint={hint} />
      {loading ? <SkeletonRows /> : children}
    </div>
  );
}

const SKELETON_WIDTHS = ['85%', '70%', '78%', '60%', '82%'];

function SkeletonRows({ count = 3 }: { count?: number }) {
  return (
    <div aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex items-center gap-[12px] px-[8px] py-[6px]">
          <span className="size-[40px] shrink-0 animate-pulse rounded-[8px] bg-gray-100" />
          <span
            className="h-[12px] animate-pulse rounded bg-gray-100"
            style={{ width: SKELETON_WIDTHS[i % SKELETON_WIDTHS.length] }}
          />
        </div>
      ))}
    </div>
  );
}

function ResultRow({
  href,
  thumbnail,
  title,
  price,
  onClick,
  id,
  active,
  onMouseEnter,
}: {
  href: string;
  thumbnail?: string;
  title: string;
  /** Present only for Listings — renders a soft price chip. */
  price?: number;
  onClick: () => void;
  /** Combobox option identity + keyboard-highlight state. */
  id?: string;
  active?: boolean;
  onMouseEnter?: () => void;
}) {
  return (
    <Link
      href={href}
      id={id}
      role="option"
      aria-selected={active}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      className={cn(
        '-mx-[8px] flex items-center gap-[12px] rounded-md px-[8px] py-[6px] transition-colors hover:bg-primary-light',
        active && 'bg-primary-light'
      )}
    >
      {thumbnail ? (
        <SafeImage
          alt={title}
          src={thumbnail}
          height={40}
          width={40}
          style={{ objectFit: 'cover' }}
          className="size-[40px] shrink-0 rounded-[8px]"
        />
      ) : (
        <span className="size-[40px] shrink-0 rounded-[8px] bg-gray-100" />
      )}
      <span className="min-w-0 flex-1 truncate text-[14px] text-gray-800">
        {title}
      </span>
      {/* `!= null` also filters out `null` (a listing with no price), which
          `!== undefined` let through to crash on .toLocaleString(). Round to
          whole dollars — the compact chip has no room for odd cents ($3,367.02). */}
      {price != null && (
        <span className="shrink-0 rounded-full bg-gray-100 px-[8px] py-[2px] text-[12px] font-medium tabular-nums text-gray-700">
          ${Math.round(price).toLocaleString()}
        </span>
      )}
    </Link>
  );
}

export default SearchBar;
